// src/utils/paddleClient.js
// Paddle.js client-side checkout implementation

/**
 * Initialize Paddle.js once on app load
 * Must be called before opening any checkouts
 */
export const initPaddle = () => {
  if (typeof window.Paddle === 'undefined') return false;

  try {
    // DO NOT include 'environment' here
    window.Paddle.Initialize({
    token: 'test_e9847087829aa869e305b4b48a5', // Your sandbox token
    eventCallback(data) {
        console.log('🔔 Paddle Event:', data.type);
        if (data.type === 'checkout.error') {
        console.error('❌ Paddle Error Detail:', data.error);
        }
    }
    });
    return true;
  } catch (error) {
    console.error('Paddle Init Error:', error);
    return false;
  }
};

/**
 * Open Paddle checkout for music purchase
 * @param {string} priceId - Paddle price ID (e.g., 'pri_01kegce41fqa6jpeh2xragn05e')
 * @param {string} email - Customer email
 * @param {object} customData - Custom metadata (musicId, downloadUrl, etc.)
 */
export const openMusicCheckout = ({ priceId, email, customData = {} }) => {
  if (typeof window.Paddle === 'undefined') {
    console.error('❌ Paddle.js not loaded');
    return;
  }

  if (!priceId) {
    console.error('❌ Price ID is required');
    return;
  }

  if (!email) {
    console.error('❌ Email is required');
    return;
  }

  try {
    console.log('🎵 Opening Paddle checkout:', {
      priceId,
      email,
      customDataKeys: Object.keys(customData)
    });

    window.Paddle.Checkout.open({
      items: [
        {
          priceId: priceId,
          quantity: 1
        }
      ],
      customer: {
        email: email
      },
      customData: {
        ...customData,
        paymentType: 'music_purchase'
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en'
      },
      onCheckoutClose() {
        console.log('User closed checkout');
      },
      onCheckoutComplete(data) {
        console.log('✅ Checkout complete:', data);
      }
    });
  } catch (error) {
    console.error('❌ Error opening checkout:', error);
    throw error;
  }
};

/**
 * Open Paddle checkout for artist subscription
 * @param {string} priceId - Paddle price ID for subscription tier
 * @param {string} email - Customer email
 * @param {object} customData - Custom metadata (userId, artistName, tier, etc.)
 */
export const openSubscriptionCheckout = ({ priceId, email, customData = {} }) => {
  if (typeof window.Paddle === 'undefined') {
    console.error('❌ Paddle.js not loaded');
    return;
  }

  if (!priceId) {
    console.error('❌ Price ID is required');
    return;
  }

  if (!email) {
    console.error('❌ Email is required');
    return;
  }

  try {
    console.log('🎁 Opening Paddle subscription checkout:', {
      priceId,
      email,
      customDataKeys: Object.keys(customData)
    });

    window.Paddle.Checkout.open({
      items: [
        {
          priceId: priceId,
          quantity: 1
        }
      ],
      customer: {
        email: email
      },
      customData: {
        ...customData,
        paymentType: 'artist_subscription'
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        locale: 'en'
      },
      onCheckoutClose() {
        console.log('User closed subscription checkout');
      },
      onCheckoutComplete(data) {
        console.log('✅ Subscription checkout complete:', data);
      }
    });
  } catch (error) {
    console.error('❌ Error opening subscription checkout:', error);
    throw error;
  }
};

/**
 * Check if Paddle is ready
 */
export const isPaddleReady = () => {
  const isReady = typeof window.Paddle !== 'undefined';
  console.log('🔍 isPaddleReady check:', {
    paddleExists: typeof window.Paddle !== 'undefined',
    paddleType: typeof window.Paddle,
    isReady
  });
  return isReady;
};

const paddleClient = {
  initPaddle,
  openMusicCheckout,
  openSubscriptionCheckout,
  isPaddleReady
};

export default paddleClient;